import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { creditCostFor, type CreditActionKey } from '@/store/slices/aiCredits';

import { Button, Icon, ProgressBar, Text } from '../../../../components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { ART_STYLE, MOBILE } from '@/nova/theme/tokens';
import { useThemeMode } from '@/nova/theme/OwsTheme';
import { CUST_GEN_DOT_FILL, CUST_GEN_DOT_FILL_LIGHT } from '@/nova/theme/customizer-tokens';
import { DotField } from '../../../customizer/components/DotField';
import { GenerateButton } from '../GenerateButton';
import { LumioMark, LumioGlyphOutline } from '../LumioMark';
import type { CreativeVersion } from '../../creative-api';
import type { NewLookPhase } from '../../new-look-api';
import { useBannerDefinition } from '../../useBannerDefinition';
import { downloadAsset, downloadAssetsZip, assetFilename, assetExt } from '../../download-asset';
import { formatProgressPct } from '../../generation-progress';
import type { CreativeFailure, CreativePreview, GenerateInput, PendingCreativeRun } from '../../useCreatives';
import { MASCOT_AUTO } from '../../../art-style/art-style-api';
import { ArtStylePicker } from '../../../art-style/components/ArtStylePicker';
import { MascotPicker } from '../../../art-style/components/MascotPicker';
import { MascotAvatar } from '../../../art-style/components/MascotAvatar';
import { artStyleLabel } from '../../../art-style/useActiveArtStyle';
import { pickedMascotOf, useStyleCast, useStyleChoices, type MascotChoice } from '../../../art-style/useStyleChoice';
import {
  Body,
  CurrentWrap,
  CurrentLabel,
  CurrentImg,
  FieldLabel,
  PromptComposer,
  AttachmentRow,
  Attachment,
  PromptArea,
  ComposerToolbar,
  AttachBtn,
  Chips,
  Chip,
  Drop,
  UploadPreview,
  Generating,
  GenBand,
  BasePreview,
  BaseChip,
  PreviewWrap,
  PreviewImg,
  ErrorNote,
  VersionPreview,
  PreviewFrame,
  PreviewChip,
  DrillHint,
  VersionTag,
  VersionActions,
  MiniBtn,
  IconMiniBtn,
  PromptToggleBtn,
  VersionView,
  FooterBtn,
  FooterBtnLabel,
  MenuWrap,
  KebabBtn,
  MenuBackdrop,
  Flyout,
  FlyoutItem,
  PublishText,
  RefRemove,
  RefHint,
  GenThumbLabel,
  GenProgress,
  GenProgressCount,
  GenThumbOverlay,
  GenThumbBar,
  GenThumbCount,
  FooterBtnCost,
  FooterBtnCostAction,
  FooterBtnCostAmount,
  SplitLayout,
  LeftPane,
  LiveTop,
  VersionScroll,
  RightPane,
  RightScroll,
  RightFooter,
  FooterRow,
  ConfirmPublishBtn,
  VersionList,
  RailDivider,
  RailInner,
  RailSpacer,
  Collapsible,
  CollapsibleInner,
  AccordionHeader,
  ArchivedHead,
  ChevronWrap,
  AccordionTitle,
  AccordionCount,
  AccordionEmpty,
  VersionRow,
  GenRow,
  BackHeader,
  BackBtn,
  HeaderTitle,
  FormatsSection,
  FormatsEmpty,
  FormatsEmptyLead,
  FormatsEmptyText,
  Provenance,
  ProvenanceHead,
  ProvenanceLabel,
  CopyBtn,
  ProvenanceText,
  ProvenanceRefs,
  ProvenanceRef,
  SourceThumb,
  LightboxBody,
  LightboxImg,
  FormatsGrid,
  FormatCard,
  FormatImg,
  FormatPlaceholderCard,
  FormatPlaceholderImg,
  FormatReveal,
  FormatBlurred,
  FormatSharp,
  FormatGenGrid,
  FormatFoot,
  FormatLabel,
  CostNote,
  ComposerChips,
  DefinitionPreview,
  DefinitionSubject,
  ShowMoreBtn,
  DefinitionRefs,
  DefinitionRef,
  DestSeg,
  DestOption,
  DrawnWithRow,
  DrawnWithChip,
  DrawnWithName,
  DrawnWithHint,
} from './EditCreativeSheet.styles';
import type { DrillDir } from './EditCreativeSheet.styles';

type EditMode = 'lumio' | 'upload';

/* The editor takes the full stage HEIGHT on desktop, and up to 1000px wide (the Sheet's own
 * `min(…, 100%)` keeps it inside the content-area overlay on narrower stages). Paired with
 * `maxHeight="100%"`, the sheet fills the stage vertically and each pane scrolls inside. Mobile stays
 * full-width (Sheet's own override). */
const PREVIEW_SHEET_WIDTH = '1000px';

/** The banner's New look (see `../../new-look-api`): its art drawn again in the brand's art style by a website
 *  regeneration, not live yet. */
export type EditCreativeNewLook = {
  status: NewLookPhase;
  /** Its art by format slot, once ready and read. */
  formats: Record<string, string> | null;
  /** The ready art is being read. */
  loading: boolean;
  /** The ready art could not be read. */
  failed: boolean;
};

export type EditCreativeSheetProps = {
  placement: string;
  placementLabel: string;
  /** The artwork of the banner being edited, shown for context at the top of the sheet. */
  currentImageUrl?: string;
  /** The banner family's version history (initial + regenerations), oldest→newest. */
  versions?: CreativeVersion[];
  /** The version that is PUBLISHED (live on the site); empty = still the initial banner. */
  publishedVersionId?: string;
  /** A regeneration is in flight against this banner: the strip shows a live "generating" tile that
   *  the finished version swaps into. Driven by the active job for this banner. */
  generating?: boolean;
  /** The KIND of the in-flight run ('formats' | 'generate' | …). A 'formats' run completes an EXISTING
   *  version, so its generating status is shown ON that version's thumb (dot-field + progress bar),
   *  not as a phantom new tile. Any other kind is a new-version regeneration (the new tile). */
  generatingKind?: string;
  /** For an in-flight 'formats' run, the id of the VERSION it is completing (the job's parent_id). Anchors
   *  the in-thumb generating overlay to the exact version, reliably across a page refresh. */
  generatingVersionId?: string;
  /** The banner's most recent run FAILED — lets the editor stop awaiting a result that will never
   *  land (a worker failure produces no new version). */
  runFailed?: boolean;
  /** Real per-format progress of the in-flight multi-slot draw (slots done / total), streamed by the
   *  backend, driving the determinate progress bar + count under the generating tile. */
  runProgress?: { completed: number; total: number };
  /** Number of distinct aspect ratios (draws) a regeneration makes for this banner — the credit
   *  cost is per-draw × this, shown to the owner up front. */
  formatCount?: number;
  /** Publish a version to the brand's live site — the pool (enqueues the publish job). */
  onPublish?: (versionId: string) => Promise<void> | void;
  /** Generate a main-only version's remaining formats (charged), so it can be viewed in every format
   *  and published. Enqueues the async 'formats' job (progress shown like a generation). */
  onGenerateFormats?: (versionId: string) => Promise<void> | void;
  /** Archive a version so it drops out of the family into "Archived". The initial / a published version
   *  can't be archived (the action is disabled for them). */
  onDelete?: (versionId: string) => Promise<void> | void;
  /** Recover an archived version back into the family. */
  onRestore?: (versionId: string) => Promise<void> | void;
  /** Live run/preview/commit state from `useCreatives` (any placement). */
  pending: PendingCreativeRun | undefined;
  preview: CreativePreview | undefined;
  committing: boolean;
  failure: CreativeFailure | undefined;
  onGenerate: (input: GenerateInput) => void | Promise<void>;
  onPlaceOwn: (dataUri: string) => void;
  onApply: () => void;
  onDiscard: () => void;
  /** Open Buy-credits when the balance can't cover an action. */
  onBuyCredits: () => void;
  onClose: () => void;
  /**
   * Whether the AI generation mode (Ask Lumio) is offered. Off for logo-family creatives, whose
   * owner-accessible edit today is a real artwork replace (Upload) through the launched-brand
   * pipeline; the AI-prompt banner path lights up when Contract B ships.
   */
  allowAiModes?: boolean;
  /** The banner's New look, shown read-only beside its versions: it goes live once the website regeneration on the
   *  Art Style page has drawn all of its art, never from here. Absent when no website regeneration covers the banner. */
  newLook?: EditCreativeNewLook;
  /** The brand and the banner being edited, to read the banner's definition (a new version can be drawn from it)
   *  and the art styles a draw can use. */
  tenantId?: number;
  bannerId?: string;
};

/** Read an uploaded file as a data URI for the spark-ai upload. */
const readAsDataUri = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });

/** Suggested one-tap briefs for the Lumio mode (English copy is the i18n key). */
const PROMPT_SUGGESTIONS = ['Restyle for the holidays', 'Make it match my logo', 'Brighter and more energetic'];

/**
 * The edit-a-creative modal. Two ways to change a placement, both inside the brand's art style:
 * describe it to Lumio, or upload your own artwork to swap the banner out. A generation runs
 * preview-first, so the result is staged (Apply to commit, Discard to drop) without ever touching
 * the live site. Every AI action is credit-gated: its cost is shown, and an unaffordable one opens
 * Buy-credits instead of running.
 */
/** Cap on reference images per regeneration, so the brief stays focused. */
/** The brief an empty composer sends when there is a banner to start from: a fresh take on it. Model-facing, so not
 *  translated; it is also what "See prompt" shows for that version. */
const REGENERATE_BRIEF = 'Regenerate';

const MAX_REFERENCES = 4;

/** The CSS aspect-ratio of a format slot (e.g. `ar21_keyright` → `2 / 1`), shared by every tile state so
 *  a slot keeps one shape across placeholder → reveal → crisp. The market-offer slots are named, not
 *  aspect tokens, so map them to their measured ratios (bonus tile 1:1, register banner 2:1). */
const slotAspectRatio = (slot: string): string => {
  if (slot === 'bonus_list') return '1 / 1';
  if (slot === 'bonus_selection') return '2 / 1';
  const digits = (slot.split('_')[0] ?? '').replace(/^ar/, '');
  return digits.length === 2 ? `${digits[0]} / ${digits[1]}` : '16 / 9';
};

/** Preferred main-visual crop, high-res-first: the 2:1 RIGHT hero convention, with a 2:1 fallback so a
 *  version missing the exact slot avoids odd/low-res crops. Matches the backend `svd.HeroSlots`. */
const HERO_SLOTS: string[] = ['ar21_keyright', 'ar21_keytop', 'ar21_keycenter'];

/** Format reveal cadence (ms). HOLD = the blurred copy fades in beneath the dot grid; DEBLUR = the grid
 *  fades out while the blurred copy cross-fades to the crisp copy. Mirrors the onboarding logo reveal
 *  (shorter, since a format tile is smaller than a logo card). */
const FORMAT_REVEAL_HOLD_MS = 480;
const FORMAT_REVEAL_DEBLUR_MS = 620;

interface FormatTileProps {
  slot: string;
  /** The drawn format's URL, or undefined while it is still a placeholder. */
  url?: string;
  /** Human label for the slot (e.g. "2:1 · right"). */
  label: string;
  /** CSS aspect-ratio for the slot's true shape. */
  ratio: string;
  /** A formats run is in flight, so an EMPTY tile shows the generating dot grid (not the static eye). */
  active: boolean;
  /** DotField seed — vary per tile so the grids never drift in lockstep. */
  seed: number;
  /** Alt text for the image (already localized by the caller). */
  imgAlt: string;
  /** Download affordance (omitted when the tile has no URL). */
  onDownload?: (slot: string, url: string) => void;
  downloadLabel: string;
}

/**
 * One ALL FORMATS tile across its whole lifecycle. A format the version ALREADY had (a seeded hero, a
 * complete version) renders crisp with no animation. A format that ARRIVES while the sheet is open
 * (undefined → url, i.e. one that just finished generating) plays the onboarding-style reveal: a blurred
 * copy fades in beneath the still-running dot grid, then the grid dissolves as the blurred copy
 * cross-fades to the crisp copy. An empty tile shows the generating dot grid while a run is active, else
 * the static placeholder marker.
 */
const FormatTile = ({
  slot,
  url,
  label,
  ratio,
  active,
  seed,
  imgAlt,
  onDownload,
  downloadLabel,
}: FormatTileProps): React.ReactElement => {
  // A url present on the FIRST render is a pre-existing format (no reveal); one that arrives later plays
  // the reveal. Captured once so the tile's mode never flips mid-reveal.
  const hadUrlAtMountRef = React.useRef(!!url);
  const animate = !!url && !hadUrlAtMountRef.current;

  const [imgLoaded, setImgLoaded] = React.useState(false);
  const [shown, setShown] = React.useState(false);
  const [deblur, setDeblur] = React.useState(false);
  const [gridGone, setGridGone] = React.useState(false);
  const sharpRef = React.useRef<HTMLImageElement>(null);
  // Dot ink must contrast the tile's plate: near-white on the dark theme, near-black on light. Empty
  // placeholders have no artwork behind them, so the default near-white would vanish on the light plate.
  const dotFill = useThemeMode() === 'light' ? CUST_GEN_DOT_FILL_LIGHT : CUST_GEN_DOT_FILL;

  // Cached-image race: a freshly-set src can already be decoded before onLoad attaches, so also check
  // `complete` once mounted (see the nova preview cached-image onLoad note).
  React.useEffect(() => {
    if (animate && sharpRef.current?.complete) setImgLoaded(true);
  }, [animate, url]);

  // Reveal sequence once the crisp copy has decoded: fade the blurred copy in beneath the grid, then
  // deblur (grid out + crossfade to sharp), then drop the grid (stops the canvas).
  React.useEffect(() => {
    if (!animate || !imgLoaded) return undefined;
    setShown(true);
    const t1 = window.setTimeout(() => setDeblur(true), FORMAT_REVEAL_HOLD_MS);
    const t2 = window.setTimeout(() => setGridGone(true), FORMAT_REVEAL_HOLD_MS + FORMAT_REVEAL_DEBLUR_MS);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [animate, imgLoaded]);

  const foot = (
    <FormatFoot>
      <FormatLabel text={label} />
      {url && onDownload ? (
        <IconMiniBtn type="button" onClick={() => onDownload(slot, url)} aria-label={downloadLabel}>
          <Icon name="download" size={12} />
        </IconMiniBtn>
      ) : null}
    </FormatFoot>
  );

  // A format that arrived while open → reveal it emerging from the dot grid. onError advances the
  // lifecycle too (a transient CDN 404 right after the write, CORS, or a network drop), so the grid
  // always dissolves and the DotField's animation loop stops instead of spinning forever.
  if (url && animate) {
    return (
      <FormatCard>
        <FormatReveal $ratio={ratio}>
          <FormatBlurred src={url} alt="" aria-hidden="true" $shown={shown} $deblur={deblur} />
          <FormatSharp
            ref={sharpRef}
            src={url}
            alt={imgAlt}
            $deblur={deblur}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgLoaded(true)}
          />
          {!gridGone ? (
            <FormatGenGrid $out={deblur}>
              <DotField seed={seed} fill={dotFill} />
            </FormatGenGrid>
          ) : null}
        </FormatReveal>
        {foot}
      </FormatCard>
    );
  }

  // A pre-existing format → crisp, no animation.
  if (url) {
    return (
      <FormatCard>
        <FormatImg src={url} alt={imgAlt} $ratio={ratio} />
        {foot}
      </FormatCard>
    );
  }

  // Empty tile → the generating dot grid while a run is active, else the static placeholder marker.
  return (
    <FormatPlaceholderCard>
      <FormatPlaceholderImg $ratio={ratio} aria-hidden="true">
        {active ? (
          <FormatGenGrid $out={false}>
            <DotField seed={seed} fill={dotFill} />
          </FormatGenGrid>
        ) : (
          <Icon name="eye" size={18} />
        )}
      </FormatPlaceholderImg>
      {foot}
    </FormatPlaceholderCard>
  );
};

/**
 * Track whether a scroll container has hidden content past its top / bottom edge, so a fade mask can
 * be applied only when there is actually something to scroll to. Recomputes on scroll, on resize, and
 * whenever `watch` changes (the content height may have changed).
 */
const useScrollFade = (
  watch: unknown,
): { ref: React.RefObject<HTMLDivElement>; fade: { top: boolean; bottom: boolean }; onScroll: () => void } => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [fade, setFade] = React.useState({ top: false, bottom: false });
  const update = React.useCallback((): void => {
    const el = ref.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const scrollable = scrollHeight - clientHeight > 1;
    setFade({
      top: scrollable && scrollTop > 1,
      bottom: scrollable && scrollTop < scrollHeight - clientHeight - 1,
    });
  }, []);
  React.useEffect(() => {
    update();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
    // `watch` re-runs this when the pane's content may have changed height.
  }, [update, watch]);
  return { ref, fade, onScroll: update };
};

export const EditCreativeSheet = ({
  placement,
  placementLabel,
  currentImageUrl,
  versions,
  publishedVersionId,
  generating = false,
  generatingKind,
  generatingVersionId,
  runFailed = false,
  runProgress,
  formatCount = 0,
  onPublish,
  onGenerateFormats,
  onDelete,
  onRestore,
  pending,
  preview,
  committing,
  failure,
  onGenerate,
  onPlaceOwn,
  onApply,
  onDiscard,
  onBuyCredits,
  onClose,
  allowAiModes = true,
  newLook,
  tenantId,
  bannerId,
}: EditCreativeSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  // `credits` (aiCredits) still supplies the per-action price table; the BALANCE now lives on the wallet
  // (owner-level CREDITS wallet). Both stay null until their backends ship — the affordability gate is
  // permissive then, exactly as before.
  const credits = useAppSelector((state) => state.aiCredits ?? null);
  const creditBalance = useAppSelector((state) => state.ownerWallet?.credits ?? null);
  // Dot ink for the generating grids over plain (artwork-free) plates — the rail thumb overlay and the
  // new-version tile. Near-white on dark, near-black on light, so the grid reads in both themes.
  const dotFill = useThemeMode() === 'light' ? CUST_GEN_DOT_FILL_LIGHT : CUST_GEN_DOT_FILL;
  // Split-view drill-down on mobile: the sheet shows the version LIST; tapping a version drills into
  // its preview + actions (a back arrow in the header returns to the list). Desktop shows both panes.
  const isMobile = useMediaQuery(MOBILE);
  const [mobileDetail, setMobileDetail] = React.useState(false);
  // Drill direction (mobile): 'in' slides the detail in from the right, 'out' the list back from left.
  const [drillDir, setDrillDir] = React.useState<DrillDir | undefined>(undefined);

  // Banners edit via Ask Lumio only; logos (no AI modes) keep their upload-replace pipeline. There
  // is no "Upload your own" toggle on banners — a raw image swap makes no sense there.
  const mode: EditMode = allowAiModes ? 'lumio' : 'upload';
  const [prompt, setPrompt] = React.useState('');
  const [upload, setUpload] = React.useState<string | undefined>(undefined);
  // Reference images (data URIs) that guide a regeneration — reproduced as closely as possible.
  const [references, setReferences] = React.useState<string[]>([]);
  // Which of the brand's mascots the new variant draws (Auto by default; the picker hides without a cast).
  // The character pick, with the style it was picked for: it counts only while the draw uses that style.
  const [mascotChoice, setMascotChoice] = React.useState<MascotChoice>({ style: undefined, pick: MASCOT_AUTO });
  // Where a new version starts: the banner's definition (a fresh draw, the default when it has one) or the selected
  // version's artwork. Unset until the owner picks, so a definition that loads later still opens on it.
  const [startChoice, setStartChoice] = React.useState<'definition' | 'current' | undefined>(undefined);
  const { definition } = useBannerDefinition(allowAiModes ? tenantId : undefined, bannerId);
  const startMode = definition?.available ? startChoice ?? 'definition' : 'current';
  const fromDefinition = startMode === 'definition';
  // The definition's subject is collapsed to a few lines; "Show more" opens it. `subjectClamped`: it overflows.
  const [subjectOpen, setSubjectOpen] = React.useState(false);
  const [subjectClamped, setSubjectClamped] = React.useState(false);
  const subjectRef = React.useRef<HTMLDivElement | null>(null);
  // Which art style the draw uses: the primary (active) one unless the owner picks another (drafts included). A
  // pick other than the active style is sent as `style_id`, and its own cast is what the character picker lists.
  // Each opening of the composer reads the styles again (and the picked one's cast), so styles created, renamed or made
  // primary on the Art Style page meanwhile are what the owner picks from, never a list read when the sheet opened.
  const [composerOpens, setComposerOpens] = React.useState(0);
  const styleChoices = useStyleChoices(allowAiModes ? tenantId : undefined, composerOpens);
  const [stylePick, setStylePick] = React.useState<string | undefined>(undefined);
  // A pick the latest list no longer has (deleted, or no longer extracted) falls back to the default.
  const pickedStyleId =
    stylePick !== undefined && (stylePick === '' || styleChoices.styles.some((style) => style.id === stylePick))
      ? stylePick
      : styleChoices.defaultId;
  // A character picked for another style (another pick, or a picked style that fell back to the default) is not this
  // style's: the draw goes back to Auto until one is picked for it.
  const drawMascot = pickedMascotOf(mascotChoice, pickedStyleId);
  const activeStyleId = styleChoices.styles.find((style) => style.status === 'active')?.id;
  const sendStyleId = pickedStyleId && pickedStyleId !== activeStyleId ? pickedStyleId : undefined;
  const pickedCast = useStyleCast(allowAiModes ? tenantId : undefined, sendStyleId, composerOpens);
  // Drag-over highlight for the composer's drop target.
  const [dragging, setDragging] = React.useState(false);
  // Publish-to-site: a two-step confirm (it replaces the live banner) + a busy state.
  const [confirmPublish, setConfirmPublish] = React.useState(false);
  // The version rail is a MUTUALLY-EXCLUSIVE accordion with EXACTLY ONE section open: opening one
  // collapses the other, and collapsing a section re-opens the other (never both closed). Default:
  // Versions open; Archived closed and pinned to the foot.
  const [openSection, setOpenSection] = React.useState<'versions' | 'archived'>('versions');
  const versionsOpen = openSection === 'versions';
  const archivedOpen = openSection === 'archived';
  // The selected version's brief is tucked behind a "See prompt" toggle rather than always shown.
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  // The "more actions" (⋮) flyout in the footer — currently just Delete version.
  const [menuOpen, setMenuOpen] = React.useState(false);
  // Reference-image lightbox (a second Sheet stacked over this one) + prompt-copy feedback.
  const [lightbox, setLightbox] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const { closeHandle: lightboxCloseHandle } = useSheetDismiss(() => setLightbox(null));
  // Optimistic submit latch: disables the CTA the instant it's clicked (before the async enqueue
  // sets `generating`), so a regeneration can't be double-submitted during the upload+enqueue round.
  const [submitting, setSubmitting] = React.useState(false);
  // The run has finished on the backend but the new version hasn't landed in the editor yet (the
  // family refetch is a beat behind the job). Keeps the "generating" indicator up and "Generate new"
  // hidden across that gap, so it can't be read as done and re-submitted.
  const [resultPending, setResultPending] = React.useState(false);
  // Browse vs compose: by default the sheet BROWSES the existing versions. "Generate new" (next to a
  // version's Download) opens the composer — its prompt lives in a SECOND Sheet stacked over this one.
  const [composing, setComposing] = React.useState(false);
  // Syncs with the DOM: whether the collapsed subject overflows its few lines (only then is "Show more" offered).
  React.useLayoutEffect(() => {
    const el = subjectRef.current;
    if (!el || subjectOpen) return;
    setSubjectClamped(el.scrollHeight > el.clientHeight + 1);
  }, [definition, fromDefinition, composing, subjectOpen]);
  const { closeHandle: composeCloseHandle } = useSheetDismiss(() => setComposing(false));
  // The publish confirmation is its own stacked Sheet (destination choice + confirm), dismissible.
  const { closeHandle: publishCloseHandle } = useSheetDismiss(() => setConfirmPublish(false));

  // Version history: the family (oldest→newest) shown as a strip when there is more than one.
  // Memoised so the effect that watches it (auto-select on regen) doesn't re-fire every render.
  const versionList = React.useMemo(() => versions ?? [], [versions]);
  const newestId = versionList.length > 0 ? versionList[versionList.length - 1].id : undefined;
  const initialId = versionList.find((v) => v.is_initial)?.id;

  // The version LIVE in the pool (what the card face + pinned "Live" thumb reflect), falling back to the
  // initial banner when nothing has been published yet.
  const publishedId = publishedVersionId ?? initialId;
  const isLive = React.useCallback(
    (v: CreativeVersion): boolean => v.id === publishedId || (!!v.is_initial && !publishedVersionId),
    [publishedId, publishedVersionId],
  );
  // The selected version drives the large preview AND is the basis a regeneration starts from.
  // Defaults to the published (live) version so the editor opens on what the card shows.
  const [selectedVersionId, setSelectedVersionId] = React.useState<string | undefined>(undefined);
  const selectedId = selectedVersionId ?? publishedId ?? newestId;
  const selectedVersion = versionList.find((v) => v.id === selectedId);
  // The artwork a regeneration edits from: the selected version, else the given current image. ALWAYS
  // the landscape hero — a redraw must edit the full banner, never a crop.
  const baseImageUrl = selectedVersion?.image_url ?? currentImageUrl;
  // What "Generate" sends: the owner's brief, or, left empty with a banner to start from, a plain regenerate.
  // A definition draw needs no brief: the banner's definition says what to show, and an empty box adds no direction.
  const composeBrief =
    prompt.trim() || (!fromDefinition && (baseImageUrl || references.length > 0) ? REGENERATE_BRIEF : '');
  const canCompose = fromDefinition || composeBrief !== '';
  // The image the modal DISPLAYS for a version — its 2:1 hero crop from the version's formats (matching
  // the card + the live page), falling back to its image_url.
  const versionVisual = React.useCallback((v: CreativeVersion): string | undefined => {
    for (const slot of HERO_SLOTS) {
      const u = v.formats?.[slot];
      if (u) return u;
    }
    return v.image_url;
  }, []);
  const previewImage = (selectedVersion ? versionVisual(selectedVersion) : undefined) ?? currentImageUrl;
  // Which slot the big preview is showing, for the "Preview 2:1 · right" chip (undefined → no chip).
  const previewSlot = React.useMemo(
    (): string | undefined => (selectedVersion ? HERO_SLOTS.find((s) => selectedVersion.formats?.[s]) : undefined),
    [selectedVersion],
  );
  // The version the SELECTED one was regenerated from (its parent in the family), for the "regenerated
  // from" source thumbnail shown with the brief. Undefined for the initial or when unresolved.
  const regeneratedFrom = selectedVersion?.parent_id
    ? versionList.find((v) => v.id === selectedVersion.parent_id)
    : undefined;
  const regeneratedFromImg = regeneratedFrom ? versionVisual(regeneratedFrom) : undefined;

  // The LIVE version is pinned on top. Beneath a hairline, the other versions list flat (newest first),
  // then an always-last, collapsible Archived section.
  const liveVersion = versionList.find((v) => v.id === publishedId);
  // The flat Versions list: every non-archived version except the pinned live one, newest first.
  const listedVersions = React.useMemo(
    () => versionList.filter((v) => !v.archived && v.id !== publishedId).reverse(),
    [versionList, publishedId],
  );
  // Archived (soft-deleted) versions get their own section, always last, newest first — recoverable.
  const archivedVersions = React.useMemo(() => versionList.filter((v) => v.archived).reverse(), [versionList]);

  // A version's list label: Live, Initial (the original banner), else Version N.
  const versionLabel = React.useCallback(
    (v: CreativeVersion): string => {
      if (isLive(v)) return t('Live');
      if (v.is_initial) return t('Initial');
      return t('Version {{n}}', { n: versionList.findIndex((x) => x.id === v.id) + 1 });
    },
    [isLive, versionList, t],
  );

  // Select a version (drives the big preview + the regeneration base). On mobile this also drills
  // into the detail pane, since the version list is the top level there.
  // The New look is viewed in place of a version (read-only); picking a version leaves it.
  const [newLookOpen, setNewLookOpen] = React.useState(false);
  const showingNewLook = newLookOpen && newLook !== undefined;
  const selectNewLook = React.useCallback((): void => {
    setNewLookOpen(true);
    setComposing(false);
    setShowPrompt(false);
    setConfirmPublish(false);
    if (isMobile) {
      setDrillDir('in');
      setMobileDetail(true);
    }
  }, [isMobile]);
  // Its main visual: the 2:1 hero crop, as a version's.
  const newLookHero = React.useMemo((): string | undefined => {
    const formats = newLook?.formats;
    if (!formats) return undefined;
    for (const slot of HERO_SLOTS) {
      if (formats[slot]) return formats[slot];
    }
    return Object.values(formats)[0];
  }, [newLook]);

  const selectVersion = React.useCallback(
    (id: string): void => {
      setSelectedVersionId(id);
      setNewLookOpen(false);
      setComposing(false);
      setShowPrompt(false);
      setConfirmPublish(false);
      if (isMobile) {
        setDrillDir('in');
        setMobileDetail(true);
      }
    },
    [isMobile],
  );

  // A run is ACTIVE from the instant Regenerate is clicked (`submitting`, optimistic, bridges the
  // enqueue round-trip), through the job running (`generating`), and until the new version actually
  // LANDS in the editor (`resultPending`, bridging the family-refetch lag after the job finishes).
  // Drives the "generating" indicators and hides "Generate new" so a run can't be read as done and
  // started twice before its result is visible.
  // A "finishing" formats run: the job is done but the family refetch has not yet caught the targeted version
  // up to its full slot set. Its OWN state (NOT the new-version `resultPending`), so the run stays classified
  // as FORMATS after `generatingKind` (a job-derived prop) clears; reusing resultPending would flip
  // newVersionRunActive true and render a phantom "new version" tile during the finish.
  const [formatsFinishingVid, setFormatsFinishingVid] = React.useState<string | undefined>(undefined);
  const runActive = submitting || generating || resultPending || !!formatsFinishingVid;
  // A "Generate all formats" run completes an EXISTING version, so its generating status belongs ON that
  // version's thumb (dot-field + progress bar), not in a phantom new tile. Everything else that runs here
  // is a new-version regeneration, which keeps the top "Generating…" tile + the detached footer bar. The
  // finishing bridge keeps it classified as formats after the job leaves the active set (so no phantom tile).
  const formatsRunActive = (generating && generatingKind === 'formats') || !!formatsFinishingVid;
  const newVersionRunActive = runActive && !formatsRunActive;
  // The version the in-thumb generating overlay anchors to. Preferred: the id the active formats job
  // reports (its parent_id) — reliable across a page refresh. Fallback (an older in-flight job with no
  // parent_id): the newest non-archived version still missing formats, which is deterministic (no
  // dependence on selection or load order) and excludes the collapsed Archived section. Derived, not
  // stored, so it can never get stuck on a stale/hidden version.
  const formatsVersionId = React.useMemo((): string | undefined => {
    if (!formatsRunActive) return undefined;
    if (generatingVersionId) return generatingVersionId;
    if (formatsFinishingVid) return formatsFinishingVid; // during the finish, the job's parent_id is gone; use the captured id
    for (let i = versionList.length - 1; i >= 0; i--) {
      const v = versionList[i];
      if (!v.archived && (v.total_formats ?? 0) > Object.keys(v.formats ?? {}).length) return v.id;
    }
    return undefined;
  }, [formatsRunActive, generatingVersionId, formatsFinishingVid, versionList]);
  // Real per-format progress for the run in flight (slots done / total), streamed by the backend.
  // Undefined until the first format lands, so the bar shows only once there is a count to report. During
  // the finish bridge (job done, awaiting the family refetch) runProgress is gone but every slot IS drawn,
  // so show a full bar rather than an empty 0% track.
  const runPct = runProgress
    ? formatProgressPct(runProgress.completed, runProgress.total)
    : formatsFinishingVid
      ? 100
      : 0;

  // One version thumbnail in the list — used for the pinned live thumb and each row below it.
  const renderVersionRow = React.useCallback(
    (v: CreativeVersion): React.ReactElement => {
      // The New look, when it is the one viewed, is the only row selected.
      const isSelected = v.id === selectedId && !showingNewLook;
      const live = isLive(v);
      const label = versionLabel(v);
      const rowImg = versionVisual(v);
      // A "Generate all formats" run in flight for THIS version: the generating status shows in context,
      // as a dot-field over the thumb with the real per-format progress bar pinned along its bottom edge.
      const genOverlay = formatsRunActive && v.id === formatsVersionId;
      return (
        <VersionRow
          key={v.id}
          type="button"
          aria-pressed={isSelected}
          $selected={isSelected}
          onClick={() => selectVersion(v.id)}
          title={label}
          aria-label={label}
        >
          {rowImg ? <img src={rowImg} alt="" loading="lazy" /> : null}
          {genOverlay ? (
            <GenThumbOverlay aria-label={t('Generating formats')}>
              <DotField seed={7} fill={dotFill} />
              <GenThumbBar>
                {runProgress ? (
                  <GenThumbCount
                    text="{{done}} of {{total}} formats"
                    placeholders={{ done: runProgress.completed, total: runProgress.total }}
                  />
                ) : null}
                <div role="progressbar" aria-valuenow={runPct} aria-valuemin={0} aria-valuemax={100}>
                  <ProgressBar value={runPct} animate />
                </div>
              </GenThumbBar>
            </GenThumbOverlay>
          ) : null}
          {live ? (
            <VersionTag $tone="current">
              <Icon name="globe" size={10} />
              <Text tag="span" text="Live" />
            </VersionTag>
          ) : v.is_initial ? (
            <VersionTag $tone="initial">
              <Text tag="span" text="Initial" />
            </VersionTag>
          ) : null}
          {/* Mobile: a corner affordance making it clear a thumb taps through to its detail view. */}
          {isMobile ? (
            <DrillHint aria-hidden="true">
              <Icon name="arrow-right-circle" size={26} />
            </DrillHint>
          ) : null}
        </VersionRow>
      );
    },
    [
      selectedId,
      showingNewLook,
      isLive,
      versionVisual,
      versionLabel,
      selectVersion,
      isMobile,
      formatsRunActive,
      formatsVersionId,
      runProgress,
      runPct,
      dotFill,
      t,
    ],
  );

  // The aspect-ratio slots a COMPLETE version has (the initial is always complete): the full target
  // slot set. A freshly generated main-only version can arrive with the backend's formats_complete /
  // total_formats absent, so completeness is derived from THIS reliable set (what a complete sibling
  // holds) minus what the selected version has actually drawn, never from those optional fields alone.
  const placeholderSlots = React.useMemo(() => {
    const complete =
      versionList.find((v) => v.is_initial && v.formats) ?? versionList.find((v) => v.formats_complete && v.formats);
    return Object.keys(complete?.formats ?? {}).sort((a, b) => a.localeCompare(b));
  }, [versionList]);
  // The target slots the selected version has NOT drawn yet (each charged to generate). Empty for a
  // complete version, or when no complete sibling exists yet to define the target set (then it can't be
  // known, so publishing is not blocked).
  const missingTargetSlots = React.useMemo(
    () => placeholderSlots.filter((slot) => !selectedVersion?.formats?.[slot]),
    [placeholderSlots, selectedVersion],
  );

  // A main-only version (fresh "Generate new") holds just its main banner; the rest of its formats must
  // be generated (charged) before it can be viewed in every format or published. The initial/live
  // version and older all-at-once versions are already complete.
  const selectedMissingFormats = missingTargetSlots.length;
  const selectedComplete = !selectedVersion || selectedMissingFormats === 0;
  // A version can be archived only if it is neither the INITIAL nor the published/live one (the backend
  // enforces the same).
  const selectedDeletable = !!selectedVersion && !selectedVersion.is_initial && !isLive(selectedVersion);
  const formatsCost = selectedMissingFormats * (creditCostFor(credits?.pricing, 'generate') ?? 0);

  // The selected version is LIVE when it's the published one — then there is nothing to publish, and the
  // footer shows the "live" marker instead of a Publish button.
  const selectedLiveEverywhere = !!selectedVersion && isLive(selectedVersion);

  // Footer composition: which secondary action (if any) sits beside "Create new version", and whether
  // the ⋮ menu has any real action for this version (it's hidden on the live/initial one — nothing to
  // archive/recover). When Create is the only control, the footer centres it at its natural width.
  const canGenerateFormats =
    !selectedLiveEverywhere &&
    !!onGenerateFormats &&
    !!selectedId &&
    !!selectedVersion &&
    !selectedComplete &&
    selectedMissingFormats > 0 &&
    !runActive;
  const canPublishSelected =
    !selectedLiveEverywhere &&
    !canGenerateFormats &&
    !!onPublish &&
    !!selectedId &&
    !!selectedVersion &&
    selectedComplete;
  const hasVersionMenu = !!selectedId && (selectedVersion?.archived ? !!onRestore : selectedDeletable && !!onDelete);
  const soloFooterCreate = !canGenerateFormats && !canPublishSelected && !hasVersionMenu;

  // When a regeneration the owner started here finishes, a new version lands at the end of the
  // family — auto-select it so the large preview swaps to the freshly generated asset. Arm on the
  // generate→running edge (recording the count), then fire when the count grows, which can be after
  // `generating` has already cleared (the family refetch lands a beat after the job goes done).
  const awaitingRef = React.useRef<{ armed: boolean; count: number }>({ armed: false, count: 0 });
  // Arm when a NEW-VERSION run actually starts (job queued/running): remember the version count so we
  // know when a new one lands, and mark the result pending so the editor keeps "generating" up. A
  // 'formats' run completes an existing version (the list never grows), so it must NOT arm this — it is
  // handled by the in-thumb overlay instead.
  React.useEffect(() => {
    if (generating && generatingKind !== 'formats' && !awaitingRef.current.armed) {
      awaitingRef.current = { armed: true, count: versionList.length };
      setResultPending(true);
    }
  }, [generating, generatingKind, versionList.length]);
  // The new variant landed: select it, end the pending state, and reset the composer for next time.
  React.useEffect(() => {
    if (awaitingRef.current.armed && versionList.length > awaitingRef.current.count) {
      const latest = versionList[versionList.length - 1];
      awaitingRef.current = { armed: false, count: 0 };
      setResultPending(false);
      if (latest) setSelectedVersionId(latest.id);
      setPrompt('');
      setReferences([]);
    }
  }, [versionList]);
  // A 'formats' run completes an EXISTING version, so the list never GROWS, and the new-version bridge above
  // can't cover it. Bridge the SAME family-refetch lag with the DEDICATED formatsFinishingVid state. ARM it
  // while a formats job is actually running (capturing the targeted version); RELEASE it once that version is
  // CONFIRMED complete in the refetched family (or the safety net below). Without this, the instant the job
  // goes 'done' runActive falls to false while the family still shows the version short a few slots, so the
  // "Generate all formats" CTA flashes back until a manual refresh (canGenerateFormats is gated on !runActive).
  React.useEffect(() => {
    if (generating && generatingKind === 'formats' && generatingVersionId) {
      setFormatsFinishingVid(generatingVersionId);
    }
  }, [generating, generatingKind, generatingVersionId]);
  React.useEffect(() => {
    if (!formatsFinishingVid) return;
    const v = versionList.find((x) => x.id === formatsFinishingVid);
    // Complete = the version now holds every target slot (the full set a complete sibling defines). Only
    // decide once that set is known, so an unknown target never strands the bridge (the safety net covers it).
    if (v && placeholderSlots.length > 0 && placeholderSlots.every((slot) => v.formats?.[slot])) {
      setFormatsFinishingVid(undefined);
    }
  }, [formatsFinishingVid, versionList, placeholderSlots]);
  // Safety net: if the family never confirms completeness (a genuinely stuck backend, past the pull-from-
  // backend poll's own window in useCreatives), don't hold "generating" forever. Armed only once the job
  // itself is done (not generating), so it can't fire mid-run.
  React.useEffect(() => {
    if (!formatsFinishingVid || generating) return undefined;
    const id = window.setTimeout(() => setFormatsFinishingVid(undefined), 20000);
    return () => window.clearTimeout(id);
  }, [formatsFinishingVid, generating]);
  // The run failed (worker failure): it will never produce a version, so stop awaiting a result.
  React.useEffect(() => {
    if (runFailed && awaitingRef.current.armed) {
      awaitingRef.current = { armed: false, count: 0 };
      setResultPending(false);
    }
    if (runFailed) setFormatsFinishingVid(undefined);
  }, [runFailed]);
  // Safety net: if a finished run's new version never lands (a rare failed family refetch), don't
  // keep "generating" up forever. Armed only in the post-finish window (result pending, no active
  // job), so it can't fire during a slow enqueue.
  React.useEffect(() => {
    if (!resultPending || generating) return undefined;
    const id = window.setTimeout(() => {
      awaitingRef.current = { armed: false, count: 0 };
      setResultPending(false);
    }, 15000);
    return () => window.clearTimeout(id);
  }, [resultPending, generating]);

  // Reset the "Copied" flash a moment after a copy.
  React.useEffect(() => {
    if (!copied) return undefined;
    const id = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);

  // Copy the selected version's prompt (so it can be reused for a future generation).
  const copyPrompt = React.useCallback(async (): Promise<void> => {
    const text = selectedVersion?.prompt;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      /* clipboard unavailable — no-op */
    }
  }, [selectedVersion]);

  // Save a reference image (so the owner can reuse it for future generations).
  const downloadRef = React.useCallback(
    (url: string): void => {
      void downloadAsset(url, assetFilename(placementLabel, 'reference', url));
    },
    [placementLabel],
  );

  // "ar21_keyright" → "2:1 · right" (aspect ratio + key-art position); download a single format. The
  // paired market-offer's named slots (bonus_list / bonus_selection) aren't aspect_position tokens, so
  // they get their own human labels (measured 1:1 and 2:1).
  const readableSlot = React.useCallback((slot: string): string => {
    if (slot === 'bonus_list') return 'Bonus tile · 1:1';
    if (slot === 'bonus_selection') return 'Register banner · 2:1';
    const [ar, pos] = slot.split('_');
    const digits = (ar ?? '').replace(/^ar/, '');
    const ratio = digits.length === 2 ? `${digits[0]}:${digits[1]}` : ar ?? slot;
    const position = pos ? pos.replace(/^key/, '') : '';
    return position ? `${ratio} · ${position}` : ratio;
  }, []);
  const downloadFormat = React.useCallback(
    (slot: string, url: string): void => {
      void downloadAsset(url, assetFilename(placementLabel, slot, url));
    },
    [placementLabel],
  );

  // The formats of the selected version, sorted for a stable gallery order.
  const selectedFormats = React.useMemo(
    () => Object.entries(selectedVersion?.formats ?? {}).sort((a, b) => a[0].localeCompare(b[0])),
    [selectedVersion],
  );

  // The full ordered slot set for the ALL FORMATS grid: every target slot (from a complete sibling)
  // UNION the selected version's own drawn slots — so a main-only version shows its seeded hero (e.g.
  // homepage's 2:1 right) as a real, filled card while the not-yet-drawn slots stay placeholders.
  const allFormatSlots = React.useMemo(() => {
    const set = new Set<string>([...placeholderSlots, ...Object.keys(selectedVersion?.formats ?? {})]);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [placeholderSlots, selectedVersion]);

  // Download every format of the selected version as ONE zip, with the banner (the hero the owner
  // reviewed) named "banner" alongside the per-slot crops. Falls back to the single hero if the CDN
  // blocks the cross-origin fetch of every image.
  const downloadAllFormats = React.useCallback(async (): Promise<void> => {
    const hero = selectedVersion?.image_url;
    const entries = [
      ...(hero ? [{ name: `banner.${assetExt(hero)}`, url: hero }] : []),
      ...selectedFormats.map(([slot, url]) => ({ name: `${slot.replace(/_/g, '-')}.${assetExt(url)}`, url })),
    ];
    if (entries.length === 0) return;
    // `<brand>-<placement>-formats.zip` (assetFilename gives a slugged name + an image ext; swap it).
    const zipName = assetFilename(placementLabel, 'formats', hero ?? 'x.png').replace(/\.[a-z0-9]+$/i, '.zip');
    const zipped = await downloadAssetsZip(entries, zipName);
    if (zipped === 0 && hero) void downloadAsset(hero, assetFilename(placementLabel, 'version', hero));
  }, [selectedVersion, selectedFormats, placementLabel]);

  const runningHere = pending?.placement === placement;
  const previewHere = preview?.placement === placement;
  const failureHere = failure?.placement === placement;

  // `submitting` only bridges the enqueue round-trip: clear it once `generating` takes over (the job
  // is queued/running) or a failure surfaces. The composer itself is closed on the Regenerate click,
  // so the editor's `runActive` tile carries the progress from here.
  React.useEffect(() => {
    if (generating || failureHere) setSubmitting(false);
  }, [generating, failureHere]);

  // Cost + affordability. Build-ahead: while the credits backend is unresolved (no balance /
  // no price), an action is NOT blocked, exactly like the header chip stays hidden.
  const costFor = (action: CreditActionKey): number | undefined => creditCostFor(credits?.pricing, action);
  const afford = (action: CreditActionKey): boolean => {
    const cost = costFor(action);
    if (creditBalance == null || cost == null) return true;
    return creditBalance >= cost;
  };
  const gatedRun = (action: CreditActionKey, run: () => void): void => {
    if (!afford(action)) {
      onBuyCredits();
      return;
    }
    run();
  };

  // "Generate new" now draws only the MAIN banner (one charged draw) so iterating is fast and cheap;
  // the remaining formats are drawn on demand and charged then. So a regeneration costs a single draw.
  const perDrawCost = costFor('generate');
  const regenCost = perDrawCost;
  const canAffordRegen = creditBalance == null || regenCost == null || creditBalance >= regenCost;

  const onPickFile = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    try {
      setUpload(await readAsDataUri(file));
    } catch {
      setUpload(undefined);
    }
  }, []);

  // Add image files as reference thumbnails (from the picker, a drop, or a paste), capped at
  // MAX_REFERENCES. Non-image files are ignored.
  const addImageFiles = React.useCallback(async (files: File[]): Promise<void> => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return;
    try {
      const uris = await Promise.all(images.map(readAsDataUri));
      setReferences((prev) => [...prev, ...uris].slice(0, MAX_REFERENCES));
    } catch {
      /* a failed read just adds nothing */
    }
  }, []);

  const onPickReferences = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const files = Array.from(event.currentTarget.files ?? []);
      event.currentTarget.value = ''; // let the same file be re-picked after a remove
      await addImageFiles(files);
    },
    [addImageFiles],
  );

  // Drop images onto the composer.
  const onDropImages = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      setDragging(false);
      void addImageFiles(Array.from(event.dataTransfer.files));
    },
    [addImageFiles],
  );

  // Paste an image into the prompt (screenshots, copied images) — attaches it instead of pasting
  // binary junk as text; plain-text pastes fall through untouched.
  const onPastePrompt = React.useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>): void => {
      const files = Array.from(event.clipboardData.items)
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => f !== null);
      if (files.length > 0) {
        event.preventDefault();
        void addImageFiles(files);
      }
    },
    [addImageFiles],
  );

  const removeReference = React.useCallback((index: number): void => {
    setReferences((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Which split panes to render: desktop shows both; mobile shows one at a time (list ↔ detail).
  const showList = !isMobile || !mobileDetail;
  const showDetail = !isMobile || mobileDetail;
  // The mobile detail view swaps the sheet title for a back arrow + banner name (banners only).
  const detailView = allowAiModes && isMobile && mobileDetail;

  // Conditional top/bottom fade masks on each scrolling pane, so content doesn't look hard-cut at the
  // sheet's padding. The `watch` string re-measures when a pane's content height may have changed.
  const leftFade = useScrollFade(`${versionList.length}|${runActive}|${liveVersion?.id ?? ''}`);
  const rightFade = useScrollFade(
    `${selectedId ?? ''}|${selectedComplete}|${showPrompt}|${confirmPublish}|${selectedFormats.length}`,
  );

  // On an accordion switch, scroll the rail to the top so the newly-opened section reveals from the top
  // (rather than the just-collapsed one leaving the view mid-scroll). Skips the initial mount.
  const railScrollRef = leftFade.ref;
  const didMountRailRef = React.useRef(false);
  React.useEffect(() => {
    if (!didMountRailRef.current) {
      didMountRailRef.current = true;
      return;
    }
    const el = railScrollRef.current;
    if (!el) return;
    const reduce =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    el.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  }, [openSection, railScrollRef]);

  // ---- body + footer by state ----
  let body: React.ReactElement;
  // ReactNode (not ReactElement): a banner being BROWSED has no footer at all.
  let footer: React.ReactNode;

  if (runningHere) {
    body = (
      <Generating>
        <GenBand aria-hidden="true" />
        <Text tag="span" text="Lumio is creating your creative. This can take a minute." />
      </Generating>
    );
    // The run continues server-side; the owner can close and it re-appears on the page.
    footer = <Button variant="outline" fullWidth label={t('Hide while it works')} onClick={dismiss} />;
  } else if (previewHere) {
    body = (
      <PreviewWrap>
        <FieldLabel text="New creative, ready to review" />
        {preview && preview.urls.length > 0 ? (
          <PreviewImg src={preview.urls[0]} alt={t('New {{name}} creative', { name: placementLabel })} />
        ) : (
          <GenBand aria-hidden="true" />
        )}
        <Text tag="span" text="Applying replaces the live creative. Your site updates on its next refresh." />
        {failureHere ? <ErrorNote text="Something went wrong applying this. Please try again." /> : null}
      </PreviewWrap>
    );
    footer = (
      <>
        <Button variant="outline" label={t('Discard')} onClick={onDiscard} disabled={committing} />
        <Button
          variant="primary"
          icon="check"
          fullWidth
          label={committing ? t('Applying…') : t('Apply to site')}
          disabled={committing}
          onClick={onApply}
        />
      </>
    );
  } else {
    body = (
      <Body>
        {/* Banners always show the version strip (so the "+" new-variant affordance is present even
            for a single-version banner); logos fall back to their current-image + upload panel. */}
        {allowAiModes ? (
          <SplitLayout>
            {showList ? (
              <LeftPane $anim={drillDir}>
                <CurrentLabel text="Current" />
                {/* What's LIVE on the current page, pinned above the scroll so it stays visible. */}
                {liveVersion ? <LiveTop>{renderVersionRow(liveVersion)}</LiveTop> : null}
                {liveVersion ? <RailDivider /> : null}
                {newLook ? (
                  <>
                    <CurrentLabel text="New look" />
                    <LiveTop>
                      <VersionRow
                        type="button"
                        aria-pressed={showingNewLook}
                        $selected={showingNewLook}
                        onClick={selectNewLook}
                        title={t('New look')}
                        aria-label={t('New look')}
                      >
                        {newLookHero ? <img src={newLookHero} alt="" loading="lazy" /> : null}
                        {newLookHero ? null : (
                          <GenThumbOverlay>
                            {newLook.status === 'drawing' ? <DotField seed={11} fill={dotFill} /> : null}
                            <GenThumbLabel>
                              {newLook.status === 'drawing' ? (
                                <Text tag="span" text="Drawing…" />
                              ) : newLook.status === 'failed' ? (
                                <Text tag="span" text="Couldn't be drawn" />
                              ) : newLook.failed ? (
                                <Text tag="span" text="Couldn't load" />
                              ) : (
                                <Text tag="span" text="Loading…" />
                              )}
                            </GenThumbLabel>
                          </GenThumbOverlay>
                        )}
                        <VersionTag $tone="initial">
                          <Text tag="span" text="Not live yet" />
                        </VersionTag>
                      </VersionRow>
                    </LiveTop>
                    <RailDivider />
                  </>
                ) : null}
                <VersionScroll
                  ref={leftFade.ref}
                  onScroll={leftFade.onScroll}
                  role="group"
                  aria-label={t('Banner versions')}
                >
                  {/* Two-section accordion: Versions (open by default), then a spacer that pins Archived
                      to the FOOT while the versions are short. Expanding Archived drops the spacer, so its
                      header rises next to Versions and its thumbnails reveal below — versions never resize. */}
                  <RailInner>
                    <AccordionHeader
                      type="button"
                      aria-expanded={versionsOpen}
                      onClick={() => setOpenSection((s) => (s === 'versions' ? 'archived' : 'versions'))}
                    >
                      <ChevronWrap $open={versionsOpen}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path
                            d="M9 6l6 6-6 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </ChevronWrap>
                      <AccordionTitle text="Versions workspace" />
                      <AccordionCount>{listedVersions.length}</AccordionCount>
                    </AccordionHeader>
                    <Collapsible $open={versionsOpen} aria-hidden={!versionsOpen}>
                      <CollapsibleInner>
                        <VersionList>
                          {/* Only a NEW-version regeneration shows the phantom "Generating…" tile — a
                              formats run renders its status ON the version's own thumb (renderVersionRow). */}
                          {newVersionRunActive ? (
                            <GenRow aria-label={t('Generating a new version')}>
                              <DotField seed={7} fill={dotFill} />
                              <GenThumbLabel>
                                <Text tag="span" text="Generating…" />
                              </GenThumbLabel>
                            </GenRow>
                          ) : null}
                          {listedVersions.length === 0 && !runActive ? (
                            <AccordionEmpty text="No other versions yet" />
                          ) : (
                            listedVersions.map(renderVersionRow)
                          )}
                        </VersionList>
                      </CollapsibleInner>
                    </Collapsible>

                    {/* Spacer only while Archived is collapsed — it holds the Archived header at the foot
                        when the versions are SHORT (when they overflow, the header's bottom-stick keeps it
                        pinned instead). Opening Archived removes it so the header moves up next to Versions. */}
                    {!archivedOpen ? <RailSpacer /> : null}

                    <ArchivedHead
                      type="button"
                      $open={archivedOpen}
                      aria-expanded={archivedOpen}
                      onClick={() => setOpenSection((s) => (s === 'archived' ? 'versions' : 'archived'))}
                    >
                      <ChevronWrap $open={archivedOpen}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path
                            d="M9 6l6 6-6 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </ChevronWrap>
                      <Icon name="trash" size={13} />
                      <AccordionTitle text="Archived" />
                      <AccordionCount>{archivedVersions.length}</AccordionCount>
                    </ArchivedHead>
                    <Collapsible $open={archivedOpen} aria-hidden={!archivedOpen}>
                      <CollapsibleInner>
                        <VersionList>
                          {archivedVersions.length === 0 ? (
                            <AccordionEmpty text="No archived versions" />
                          ) : (
                            archivedVersions.map(renderVersionRow)
                          )}
                        </VersionList>
                      </CollapsibleInner>
                    </Collapsible>
                  </RailInner>
                </VersionScroll>
                {/* Detached footer progress for a NEW-version run (slots done / total), once it reports. A
                    formats run shows its progress in-thumb instead, so it is excluded here. */}
                {newVersionRunActive && runProgress ? (
                  <GenProgress>
                    <GenProgressCount
                      text="{{done}} of {{total}} formats"
                      placeholders={{ done: runProgress.completed, total: runProgress.total }}
                    />
                    <div role="progressbar" aria-valuenow={runPct} aria-valuemin={0} aria-valuemax={100}>
                      <ProgressBar value={runPct} animate />
                    </div>
                  </GenProgress>
                ) : null}
              </LeftPane>
            ) : null}
            {showDetail ? (
              <RightPane $anim={drillDir}>
                {showingNewLook && newLook ? (
                  <RightScroll
                    ref={rightFade.ref}
                    onScroll={rightFade.onScroll}
                    $fadeTop={rightFade.fade.top}
                    $fadeBottom={rightFade.fade.bottom}
                  >
                    <VersionView key="new-look">
                      {newLookHero ? (
                        <PreviewFrame>
                          <VersionPreview src={newLookHero} alt={t('{{name}} new look', { name: placementLabel })} />
                        </PreviewFrame>
                      ) : null}
                      <FormatsEmpty>
                        <FormatsEmptyLead>
                          <LumioMark size={14} />
                          {newLook.status === 'drawing' ? (
                            <Text tag="span" text="Drawing this banner in your art style" />
                          ) : newLook.status === 'ready' ? (
                            <Text tag="span" text="Drawn in your art style, not live yet" />
                          ) : (
                            <Text tag="span" text="This banner's new look couldn't be drawn" />
                          )}
                        </FormatsEmptyLead>
                        {newLook.status === 'drawing' ? (
                          <FormatsEmptyText text="Your website art is being redrawn. This banner's new look shows here as soon as it's done." />
                        ) : newLook.status === 'failed' ? (
                          <FormatsEmptyText text="You can retry the regeneration on the Art Style page." />
                        ) : newLook.failed ? (
                          <FormatsEmptyText text="We couldn't load this new look. Please try again later." />
                        ) : (
                          <FormatsEmptyText text="It goes live with the rest of your website art once all of it is drawn." />
                        )}
                      </FormatsEmpty>
                      {newLook.formats && Object.keys(newLook.formats).length > 0 ? (
                        <FormatsSection>
                          <CurrentLabel text="All formats" />
                          <FormatsGrid>
                            {Object.keys(newLook.formats)
                              .sort()
                              .map((slot, i) => (
                                <FormatTile
                                  key={`new-look-${slot}`}
                                  slot={slot}
                                  url={newLook.formats?.[slot]}
                                  label={readableSlot(slot)}
                                  ratio={slotAspectRatio(slot)}
                                  active={false}
                                  seed={i + 1}
                                  imgAlt={t('{{ratio}} format', { ratio: readableSlot(slot) })}
                                  onDownload={downloadFormat}
                                  downloadLabel={t('Download {{name}}', { name: readableSlot(slot) })}
                                />
                              ))}
                          </FormatsGrid>
                        </FormatsSection>
                      ) : null}
                    </VersionView>
                  </RightScroll>
                ) : (
                  <>
                    <RightScroll
                      ref={rightFade.ref}
                      onScroll={rightFade.onScroll}
                      $fadeTop={rightFade.fade.top}
                      $fadeBottom={rightFade.fade.bottom}
                    >
                      <VersionView key={selectedId ?? 'none'}>
                        {/* Big preview of the selected version — its surface hero crop (homepage 2:1 right,
                        promotions 2:1 center), tagged with which crop it is. */}
                        {previewImage ? (
                          <PreviewFrame>
                            <VersionPreview src={previewImage} alt={t('{{name}} version', { name: placementLabel })} />
                            {previewSlot ? (
                              <PreviewChip
                                text="Preview {{format}}"
                                placeholders={{ format: readableSlot(previewSlot) }}
                              />
                            ) : null}
                          </PreviewFrame>
                        ) : null}
                        <VersionActions>
                          {selectedVersion?.image_url ? (
                            <MiniBtn
                              type="button"
                              onClick={() => {
                                // Multi-format versions download a zip of every format (banner + per-slot crops);
                                // a single-format one just saves its image.
                                if (selectedFormats.length > 1) {
                                  void downloadAllFormats();
                                  return;
                                }
                                const url = selectedVersion.image_url;
                                if (url) void downloadAsset(url, assetFilename(placementLabel, 'version', url));
                              }}
                            >
                              <Icon name="download" size={13} />
                              <Text tag="span" text="Download all formats (.zip)" />
                            </MiniBtn>
                          ) : null}
                          {/* The brief this version was made from, tucked behind a toggle (pushed right). */}
                          {selectedVersion?.prompt ? (
                            <PromptToggleBtn
                              type="button"
                              onClick={() => setShowPrompt((v) => !v)}
                              aria-expanded={showPrompt}
                            >
                              <Icon name="chevron-expand" size={13} />
                              {showPrompt ? (
                                <Text tag="span" text="Hide prompt" />
                              ) : (
                                <Text tag="span" text="See prompt" />
                              )}
                            </PromptToggleBtn>
                          ) : null}
                        </VersionActions>
                        {/* The brief + any reference images, revealed by "See prompt". */}
                        {showPrompt &&
                        selectedVersion &&
                        (selectedVersion.prompt ||
                          (selectedVersion.references?.length ?? 0) > 0 ||
                          selectedVersion.art_style?.style_id) ? (
                          <Provenance>
                            {selectedVersion.prompt ? (
                              <>
                                <ProvenanceHead>
                                  <ProvenanceLabel text="Generated from" />
                                  <CopyBtn
                                    type="button"
                                    onClick={() => void copyPrompt()}
                                    aria-label={t('Copy prompt')}
                                  >
                                    <Icon name={copied ? 'check' : 'copy'} size={12} />
                                    {copied ? <Text tag="span" text="Copied" /> : <Text tag="span" text="Copy" />}
                                  </CopyBtn>
                                </ProvenanceHead>
                                <ProvenanceText text={selectedVersion.prompt} />
                              </>
                            ) : null}
                            {selectedVersion.references && selectedVersion.references.length > 0 ? (
                              <ProvenanceRefs>
                                {selectedVersion.references.map((url, i) => (
                                  <ProvenanceRef
                                    key={i}
                                    type="button"
                                    onClick={() => setLightbox(url)}
                                    aria-label={t('Open reference image')}
                                  >
                                    <img src={url} alt={t('Reference image')} />
                                  </ProvenanceRef>
                                ))}
                              </ProvenanceRefs>
                            ) : null}
                            {/* The art style and the mascot this version was drawn with (as named then). */}
                            {selectedVersion.art_style?.style_id ? (
                              <>
                                <ProvenanceLabel text="Drawn with" />
                                <DrawnWithRow>
                                  <DrawnWithChip>
                                    <Icon name="palette" size={13} />
                                    <Text tag="span" text="Art style:" />
                                    <DrawnWithName
                                      raw
                                      text={
                                        artStyleLabel(selectedVersion.art_style.style_name ?? '') || t('Your art style')
                                      }
                                    />
                                  </DrawnWithChip>
                                  <DrawnWithChip>
                                    <MascotAvatar
                                      url={selectedVersion.art_style.mascot_avatar}
                                      size={ART_STYLE.MASCOT_PILL_AVATAR_PX}
                                      muted={!selectedVersion.art_style.mascot_id}
                                    />
                                    {selectedVersion.art_style.mascot_id ? (
                                      <>
                                        <Text tag="span" text="Character:" />
                                        <DrawnWithName
                                          raw
                                          text={selectedVersion.art_style.mascot_name || t('Unnamed character')}
                                        />
                                        {!selectedVersion.art_style.mascot_pick ? (
                                          <DrawnWithHint text="(auto)" />
                                        ) : null}
                                      </>
                                    ) : (
                                      <Text tag="span" text="No character" />
                                    )}
                                  </DrawnWithChip>
                                </DrawnWithRow>
                              </>
                            ) : null}
                            {/* The banner this version was regenerated from (its parent), so the brief +
                            the source it started from read together. */}
                            {regeneratedFromImg ? (
                              <>
                                <ProvenanceLabel text="Regenerated from" />
                                <SourceThumb
                                  type="button"
                                  onClick={() => setLightbox(regeneratedFromImg)}
                                  aria-label={t('Open the source image')}
                                >
                                  <img src={regeneratedFromImg} alt={t('Source image')} />
                                </SourceThumb>
                              </>
                            ) : null}
                          </Provenance>
                        ) : null}
                        {/* Every format of the selected version, listed directly (one image per aspect ratio),
                        with a clear empty state + placeholder cards when a main-only version's formats
                        haven't been drawn yet (the "Generate all formats" CTA lives in the footer). */}
                        <FormatsSection>
                          <CurrentLabel text="All formats" />
                          {/* A main-only version still has formats to draw — explain before the grid, whose
                          already-drawn slots (the seeded hero) show filled and the rest as placeholders. */}
                          {!selectedComplete ? (
                            <FormatsEmpty>
                              {onGenerateFormats &&
                              selectedId &&
                              selectedVersion &&
                              selectedMissingFormats > 0 &&
                              !runActive ? (
                                <>
                                  <FormatsEmptyLead>
                                    <Icon name="globe" size={14} />
                                    <Text tag="span" text="Publishing is only possible if all formats are generated" />
                                  </FormatsEmptyLead>
                                  <FormatsEmptyText text="This version only has its main banner so far. Generate its other formats to use it across every spot on your site." />
                                </>
                              ) : runActive ? (
                                <FormatsEmptyText text="Generating this version's formats…" />
                              ) : (
                                <FormatsEmptyText text="No formats for this version yet." />
                              )}
                            </FormatsEmpty>
                          ) : null}
                          {/* One grid over the full slot set: each slot the version has drawn is a real,
                          downloadable card; the rest are placeholders showing what is still coming. */}
                          {allFormatSlots.length > 0 ? (
                            <FormatsGrid>
                              {/* Keyed by version + slot so switching versions REMOUNTS the tiles: a tile
                              only plays the reveal when a format ARRIVES for the version being viewed
                              (undefined→url mid-run), never when selection flips to a complete version. */}
                              {allFormatSlots.map((slot, i) => (
                                <FormatTile
                                  key={`${selectedVersion?.id ?? ''}-${slot}`}
                                  slot={slot}
                                  url={selectedVersion?.formats?.[slot]}
                                  label={readableSlot(slot)}
                                  ratio={slotAspectRatio(slot)}
                                  active={formatsRunActive}
                                  seed={i + 1}
                                  imgAlt={t('{{ratio}} format', { ratio: readableSlot(slot) })}
                                  onDownload={downloadFormat}
                                  downloadLabel={t('Download {{name}}', { name: readableSlot(slot) })}
                                />
                              ))}
                            </FormatsGrid>
                          ) : null}
                        </FormatsSection>
                      </VersionView>
                    </RightScroll>
                    {/* Fixed action footer: Create + the adaptive Publish slot side by side, always visible
                    while the content scrolls above. Publishing opens a confirm modal (destination + confirm). */}
                    <RightFooter>
                      <FooterRow $center={soloFooterCreate}>
                        <FooterBtn
                          $tone="outline"
                          $fit={soloFooterCreate}
                          type="button"
                          disabled={runActive}
                          onClick={() => {
                            if (!canAffordRegen) {
                              onBuyCredits();
                              return;
                            }
                            setComposing(true);
                            setComposerOpens((n) => n + 1);
                          }}
                        >
                          <LumioMark size={18} />
                          <FooterBtnLabel text="Create new version from this one" />
                        </FooterBtn>
                        {canGenerateFormats && selectedId ? (
                          <FooterBtn $tone="primary" type="button" onClick={() => void onGenerateFormats?.(selectedId)}>
                            <LumioGlyphOutline size={17} />
                            {/* The credits suffix never truncates — only the action label ellipsises — so owners
                            always see the cost even when the button is tight. */}
                            <FooterBtnCost>
                              <FooterBtnCostAction text="Generate all formats" />
                              <FooterBtnCostAmount
                                text={formatsCost === 1 ? '· {{n}} credit' : '· {{n}} credits'}
                                placeholders={{ n: formatsCost }}
                              />
                            </FooterBtnCost>
                          </FooterBtn>
                        ) : canPublishSelected ? (
                          <FooterBtn $tone="primary" type="button" onClick={() => setConfirmPublish(true)}>
                            <Icon name="globe" size={15} />
                            <FooterBtnLabel text="Publish to site" />
                          </FooterBtn>
                        ) : null}
                        {/* "More actions" (⋮): Archive an active version, or Recover an archived one. Hidden on
                        the live/initial version, which has no archive/recover action. */}
                        {hasVersionMenu && selectedId ? (
                          <MenuWrap>
                            <KebabBtn
                              type="button"
                              aria-haspopup="menu"
                              aria-expanded={menuOpen}
                              aria-label={t('More actions')}
                              onClick={() => setMenuOpen((o) => !o)}
                            >
                              <Icon name="more" size={20} />
                            </KebabBtn>
                            {menuOpen ? (
                              <>
                                <MenuBackdrop onClick={() => setMenuOpen(false)} />
                                <Flyout role="menu">
                                  {selectedVersion?.archived ? (
                                    <FlyoutItem
                                      type="button"
                                      role="menuitem"
                                      $tone="neutral"
                                      onClick={() => {
                                        setMenuOpen(false);
                                        void onRestore?.(selectedId);
                                      }}
                                    >
                                      <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                      >
                                        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                        <path d="M3 3v5h5" />
                                      </svg>
                                      <Text tag="span" text="Recover version" />
                                    </FlyoutItem>
                                  ) : (
                                    <FlyoutItem
                                      type="button"
                                      role="menuitem"
                                      disabled={!selectedDeletable}
                                      title={
                                        selectedDeletable
                                          ? undefined
                                          : t("Live and original versions can't be archived")
                                      }
                                      onClick={() => {
                                        if (!selectedDeletable) return;
                                        setMenuOpen(false);
                                        setSelectedVersionId(undefined); // archived one leaves the surface list
                                        void onDelete?.(selectedId);
                                      }}
                                    >
                                      <Icon name="trash" size={15} />
                                      <Text tag="span" text="Archive" />
                                    </FlyoutItem>
                                  )}
                                </Flyout>
                              </>
                            ) : null}
                          </MenuWrap>
                        ) : null}
                      </FooterRow>
                    </RightFooter>
                  </>
                )}
              </RightPane>
            ) : null}
          </SplitLayout>
        ) : currentImageUrl ? (
          <CurrentWrap>
            <CurrentLabel text="Current banner" />
            <CurrentImg src={currentImageUrl} alt={t('Current {{name}} creative', { name: placementLabel })} />
          </CurrentWrap>
        ) : null}

        {allowAiModes ? null : <FieldLabel text="Upload your own artwork and Lumio fits it to this spot." />}

        {/* Logos swap via an inline upload; banners compose a new variant in a separate stacked
            Sheet (opened by "Generate new"), so there is no banner composer here. */}
        {mode === 'upload' ? (
          <div>
            <FieldLabel text="Upload your own image to swap this banner out" />
            {upload ? (
              <UploadPreview src={upload} alt={t('Your uploaded creative')} />
            ) : (
              <Drop>
                <Icon name="plus-circle" size={22} />
                <Text tag="span" text="Choose an image to place in this spot" />
                <input type="file" accept="image/*" onChange={(event) => void onPickFile(event)} />
              </Drop>
            )}
          </div>
        ) : null}
      </Body>
    );

    // Banners: no footer on the main (browse) sheet — the Regenerate CTA lives in the composer
    // Sheet. Logos: the inline Place-image action.
    if (mode === 'lumio') {
      footer = null;
    } else {
      footer = (
        <GenerateButton
          label={committing ? t('Placing…') : t('Place image')}
          cost={costFor('upload_place')}
          disabled={!upload || committing}
          onClick={() => gatedRun('upload_place', () => upload && onPlaceOwn(upload))}
        />
      );
    }
  }

  // The new-variant composer (rendered as a stacked Sheet below). It always draws a NEW version from
  // the selected one, so its CTA reads "Generate".

  return (
    <>
      <Sheet
        // Mobile detail: swap the title for a back arrow + banner name (returns to the version list).
        title={detailView ? undefined : 'Edit {{placement}}'}
        titlePlaceholders={detailView ? undefined : { placement: placementLabel }}
        header={
          detailView ? (
            <BackHeader>
              <BackBtn
                type="button"
                onClick={() => {
                  setDrillDir('out');
                  setMobileDetail(false);
                }}
                aria-label={t('Back to versions')}
              >
                <Icon name="arrow-left" size={22} />
              </BackBtn>
              <HeaderTitle text={placementLabel} />
            </BackHeader>
          ) : undefined
        }
        ariaLabel="Edit {{placement}}"
        ariaLabelPlaceholders={{ placement: placementLabel }}
        onClose={onClose}
        closeHandle={closeHandle}
        busy={committing}
        animateResize
        maxWidth={PREVIEW_SHEET_WIDTH}
        maxHeight="100%"
        footer={footer}
      >
        {body}
      </Sheet>
      {/* The new-variant composer: prompt + references + Regenerate, in its OWN Sheet stacked over
          the editor. Opened by "Generate new"; closes itself when the new variant lands. */}
      {composing && allowAiModes ? (
        <Sheet
          title="New variant"
          ariaLabel="New variant"
          onClose={() => setComposing(false)}
          closeHandle={composeCloseHandle}
          animateResize
          footer={
            <GenerateButton
              label={runActive ? t('Generating…') : t('Generate')}
              cost={runActive ? undefined : regenCost}
              disabled={runActive || !canCompose}
              onClick={() => {
                if (runActive || !canCompose) return;
                if (!canAffordRegen) {
                  onBuyCredits();
                  return;
                }
                setSubmitting(true);
                // Hand off to the editor: close the composer immediately; the editor's "generating"
                // tile (driven by runActive) carries the progress until the new variant lands.
                setComposing(false);
                void onGenerate({
                  mode: 'generate',
                  prompt: composeBrief,
                  referenceDataUris: references.length > 0 ? references : undefined,
                  // A draw from the definition is fresh: no artwork to edit from (the key is kept, so the caller's
                  // own base image does not fill it in).
                  baseImageUrl: fromDefinition ? undefined : baseImageUrl,
                  useDefinition: fromDefinition || undefined,
                  styleId: sendStyleId,
                  parentVersionId: selectedId,
                  mascotId: drawMascot || undefined,
                });
              }}
            />
          }
        >
          <Body>
            {/* Where the draw starts, when the banner has a definition: from it (a fresh draw) or from the
                selected version. */}
            {definition?.available ? (
              <DestSeg role="radiogroup" aria-label={t('Start from')}>
                <DestOption
                  type="button"
                  role="radio"
                  aria-checked={fromDefinition}
                  $active={fromDefinition}
                  onClick={() => setStartChoice('definition')}
                >
                  <Text tag="span" text="From its definition" />
                </DestOption>
                <DestOption
                  type="button"
                  role="radio"
                  aria-checked={!fromDefinition}
                  $active={!fromDefinition}
                  onClick={() => setStartChoice('current')}
                >
                  <Text tag="span" text="From the current banner" />
                </DestOption>
              </DestSeg>
            ) : null}
            {/* From its definition: what the definition shows, read-only, with its own reference images. */}
            {fromDefinition && definition ? (
              <DefinitionPreview>
                <DefinitionSubject ref={subjectRef} $open={subjectOpen}>
                  <Text raw tag="span" text={[definition.subject, ...definition.lines].join('\n')} />
                </DefinitionSubject>
                {subjectClamped || subjectOpen ? (
                  <ShowMoreBtn type="button" aria-expanded={subjectOpen} onClick={() => setSubjectOpen((v) => !v)}>
                    {subjectOpen ? <Text tag="span" text="Show less" /> : <Text tag="span" text="Show more" />}
                  </ShowMoreBtn>
                ) : null}
                {definition.references.length > 0 ? (
                  <DefinitionRefs>
                    {definition.references.map((ref) => (
                      <DefinitionRef
                        key={ref.url}
                        src={ref.url}
                        alt={ref.name}
                        title={ref.name}
                        loading="lazy"
                        decoding="async"
                      />
                    ))}
                  </DefinitionRefs>
                ) : null}
              </DefinitionPreview>
            ) : null}
            {/* The version this variant starts from, for context (the editor is hidden behind), tagged
                so the owner knows the new draw begins from it. */}
            {baseImageUrl && !fromDefinition ? (
              <BasePreview>
                <VersionPreview src={baseImageUrl} alt={t('{{name}} version', { name: placementLabel })} />
                <BaseChip text="Regenerates from" />
              </BasePreview>
            ) : null}
            <FieldLabel text="Describe the creative you want" />
            {/* Chat-style composer: attachments inline, plus drag-and-drop onto the box and paste
                into the field. */}
            <PromptComposer
              $dragging={dragging}
              onDragOver={(event) => {
                event.preventDefault();
                if (!dragging) setDragging(true);
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
              }}
              onDrop={onDropImages}
            >
              {references.length > 0 ? (
                <AttachmentRow>
                  {references.map((uri, i) => (
                    <Attachment key={i}>
                      <img src={uri} alt={t('Reference image')} />
                      <RefRemove
                        type="button"
                        aria-label={t('Remove reference image')}
                        onClick={() => removeReference(i)}
                      >
                        <Icon name="close" size={11} />
                      </RefRemove>
                    </Attachment>
                  ))}
                </AttachmentRow>
              ) : null}
              <PromptArea
                value={prompt}
                onChange={(event) => setPrompt(event.currentTarget.value)}
                onPaste={onPastePrompt}
                placeholder={t('e.g. A festive Día de Muertos hero with marigolds and warm lights')}
                aria-label={t('Describe the creative you want')}
              />
              <ComposerToolbar>
                {references.length < MAX_REFERENCES ? (
                  <AttachBtn>
                    <Icon name="plus" size={14} />
                    <Text tag="span" text="Add image" />
                    <input type="file" accept="image/*" multiple onChange={(event) => void onPickReferences(event)} />
                  </AttachBtn>
                ) : null}
              </ComposerToolbar>
              {/* The style and the mascot this draw uses, side by side on their own row. */}
              <ComposerChips>
                <ArtStylePicker
                  styles={styleChoices.styles}
                  value={pickedStyleId ?? ''}
                  allowNone={activeStyleId === undefined}
                  // Another style has another cast: the character pick (made for the previous one) no longer counts.
                  onChange={setStylePick}
                />
                <MascotPicker
                  value={drawMascot}
                  onChange={(pick) => setMascotChoice({ style: pickedStyleId, pick })}
                  align="start"
                  cast={sendStyleId ? pickedCast ?? [] : undefined}
                />
              </ComposerChips>
            </PromptComposer>
            <Chips>
              {PROMPT_SUGGESTIONS.map((s) => (
                <Chip key={s} type="button" onClick={() => setPrompt(t(s))}>
                  {t(s)}
                </Chip>
              ))}
            </Chips>
            <RefHint text="Drop, paste, or attach images to guide the result; we keep them as close as we can." />
            {formatCount > 1 && perDrawCost != null ? (
              <CostNote text="This draws just the main banner so you can iterate fast. Generate its other formats when you're ready to publish." />
            ) : null}
          </Body>
        </Sheet>
      ) : null}
      {/* A reference image full-size, in its OWN Sheet stacked over the editor (the app's native
          stacked-modal pattern), with the option to save it for reuse in future generations. */}
      {lightbox ? (
        <Sheet
          title="Reference image"
          ariaLabel="Reference image"
          onClose={() => setLightbox(null)}
          closeHandle={lightboxCloseHandle}
          footer={
            <Button
              variant="primary"
              icon="download"
              fullWidth
              label={t('Download')}
              onClick={() => downloadRef(lightbox)}
            />
          }
        >
          <LightboxBody>
            <LightboxImg src={lightbox} alt={t('Reference image')} />
          </LightboxBody>
        </Sheet>
      ) : null}
      {/* Publish confirmation, in its OWN Sheet stacked over the editor. Publishing makes the version live
          in the pool (the Promotions page); which carousels show it is curated separately. */}
      {confirmPublish && onPublish && selectedId ? (
        <Sheet
          title="Publish to your live site"
          ariaLabel="Publish to your live site"
          onClose={() => setConfirmPublish(false)}
          closeHandle={publishCloseHandle}
          footer={
            <ConfirmPublishBtn
              type="button"
              disabled={publishing}
              onClick={async () => {
                setPublishing(true);
                try {
                  await onPublish(selectedId);
                } finally {
                  setPublishing(false);
                  setConfirmPublish(false);
                }
              }}
            >
              <Icon name="globe" size={15} />
              {publishing ? <Text tag="span" text="Publishing…" /> : <Text tag="span" text="Publish to site" />}
            </ConfirmPublishBtn>
          }
        >
          <Body>
            <PublishText text="Publish this version to your live site? It replaces what is currently live in the pool." />
          </Body>
        </Sheet>
      ) : null}
    </>
  );
};

export default EditCreativeSheet;
